import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { vi } from "date-fns/locale";
import { motion } from "framer-motion";
import {
  AlertTriangle,
  ChevronRight,
  ClipboardList,
  Clock,
  FileCheck,
  List,
  RefreshCw,
} from "lucide-react";
import {
  getAllTasks,
  getCurrentApproversBatch,
} from "../../api/hrm/tasks/task_info";
import { hasPermission } from "../../utils/permissionUtils";
import { HUB_PREVIEW_LIMIT, WORK_FULL_LIST_PATH } from "./mobileNavConfig";
import {
  MobilePageShell,
  MobilePageHeader,
  MobileSectionHeader,
  MobileNavGroup,
  MobileNavRow,
} from "./MobileNavPrimitives";

function todayIso() {
  return format(new Date(), "yyyy-MM-dd");
}

function monthRange() {
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth(), 1);
  const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return {
    from_date: format(first, "yyyy-MM-dd"),
    to_date: format(last, "yyyy-MM-dd"),
  };
}

function statusMeta(status) {
  const s = String(status || "").toLowerCase();
  if (s === "waiting")
    return { label: "Chờ duyệt", tone: "amber", chip: "bg-amber-50 text-amber-700 border-amber-100" };
  if (s === "reported")
    return { label: "Đã báo cáo", tone: "violet", chip: "bg-violet-50 text-violet-700 border-violet-100" };
  if (s === "approved")
    return { label: "Đã duyệt", tone: "sky", chip: "bg-sky-50 text-sky-700 border-sky-100" };
  if (s === "completed")
    return { label: "Hoàn thành", tone: "emerald", chip: "bg-emerald-50 text-emerald-700 border-emerald-100" };
  if (s.includes("reject"))
    return { label: "Từ chối", tone: "rose", chip: "bg-rose-50 text-rose-700 border-rose-100" };
  return { label: status || "—", tone: "slate", chip: "bg-slate-50 text-slate-600 border-slate-100" };
}

function formatWorkDay(day) {
  if (!day) return "";
  try {
    return format(new Date(day), "dd/MM", { locale: vi });
  } catch {
    return String(day).slice(5);
  }
}

function taskTitle(t) {
  return (
    t.taskname ||
    t.title ||
    t.task_name ||
    (Array.isArray(t.task_list) && t.task_list[0]?.taskname) ||
    `Công việc #${t.id}`
  );
}

function StatCard({ label, value, icon: Icon, tone, onClick }) {
  const tones = {
    rose: "bg-rose-50 border-rose-100 text-rose-700",
    amber: "bg-amber-50 border-amber-100 text-amber-700",
    violet: "bg-violet-50 border-violet-100 text-violet-700",
    sky: "bg-sky-50 border-sky-100 text-sky-700",
    emerald: "bg-emerald-50 border-emerald-100 text-emerald-700",
  };
  const t = tones[tone] || tones.sky;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex min-h-[96px] flex-col rounded-2xl border p-3.5 text-left shadow-sm active:scale-[0.98] transition-transform ${t}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/70">
          <Icon size={16} />
        </div>
        <ChevronRight size={14} className="opacity-40" />
      </div>
      <p className="mt-2.5 text-[11px] font-semibold opacity-80">{label}</p>
      <p className="mt-0.5 text-2xl font-black tabular-nums text-gray-900">{value}</p>
    </button>
  );
}

function TaskRow({ task, onClick }) {
  const meta = statusMeta(task.status);
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-start gap-3 px-4 py-3.5 text-left active:bg-gray-50"
    >
      <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-rose-50 text-rose-700">
        <ClipboardList size={16} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-gray-800">
          {taskTitle(task)}
        </p>
        <p className="mt-0.5 truncate text-[11px] text-gray-400">
          {[task.employee_name, formatWorkDay(task.work_day)]
            .filter(Boolean)
            .join(" · ")}
        </p>
        <span
          className={`mt-1.5 inline-flex rounded-full border px-2 py-0.5 text-[10px] font-semibold ${meta.chip}`}
        >
          {meta.label}
        </span>
      </div>
      <ChevronRight size={14} className="mt-2 shrink-0 text-gray-300" />
    </button>
  );
}

export default function WorkHubScreen() {
  const navigate = useNavigate();
  const canCreate = hasPermission("hrm.tasks", "C");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [error, setError] = useState("");

  const currentUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  }, []);

  const load = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    setError("");
    try {
      const range = monthRange();
      const data = await getAllTasks({ ...range, summary_mode: 1 });
      const items = Array.isArray(data?.items) ? data.items : [];

      const candidates = items.filter((t) => {
        const s = String(t.status || "").toLowerCase();
        return s === "waiting" || s === "reported";
      });
      const ids = [...new Set(candidates.map((t) => t.id).filter(Boolean))];

      let approverMap = {};
      if (ids.length > 0) {
        try {
          const batchRes = await getCurrentApproversBatch(ids);
          (batchRes?.data || []).forEach((item) => {
            approverMap[item.task_id] = item.approvers || [];
          });
        } catch {
          approverMap = {};
        }
      }

      const enriched = items.map((t) => {
        const approvers = approverMap[t.id] || [];
        const canApprove = approvers.some(
          (a) => String(a.id) === String(currentUser?.id)
        );
        return { ...t, can_approve: canApprove };
      });

      setTasks(enriched);
    } catch (e) {
      console.error(e);
      setError("Không tải được công việc. Thử lại sau.");
      setTasks([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [currentUser?.id]);

  useEffect(() => {
    load(false);
  }, [load]);

  const today = todayIso();

  const needApprove = useMemo(
    () =>
      tasks.filter((t) => {
        const s = String(t.status || "").toLowerCase();
        return t.can_approve && (s === "waiting" || s === "reported");
      }),
    [tasks]
  );

  const todayOpen = useMemo(
    () =>
      tasks.filter((t) => {
        const day = String(t.work_day || "").slice(0, 10);
        const s = String(t.status || "").toLowerCase();
        return (
          day === today &&
          s !== "completed" &&
          !s.includes("reject")
        );
      }),
    [tasks, today]
  );

  const counts = useMemo(() => {
    const by = (status) =>
      tasks.filter((t) => String(t.status || "").toLowerCase() === status)
        .length;
    return {
      needApprove: needApprove.length,
      waiting: by("waiting"),
      reported: by("reported"),
      today: todayOpen.length,
      completed: by("completed"),
    };
  }, [tasks, needApprove, todayOpen]);

  const openFullList = () => navigate(WORK_FULL_LIST_PATH);
  const goNeedApprove = () => navigate("/m/work/need-approve");
  const goToday = () => navigate("/m/work/today");
  const goWaiting = () => navigate("/m/work/waiting");
  const goReported = () => navigate("/m/work/reported");

  const dateLabel = format(new Date(), "MMMM yyyy", { locale: vi });

  return (
    <MobilePageShell>
      <MobilePageHeader
        title="Công việc"
        subtitle={`Tháng ${dateLabel} · Việc cần xử lý`}
        tone="rose"
        trailing={
          <button
            type="button"
            onClick={() => load(true)}
            disabled={refreshing || loading}
            className="flex h-10 w-10 items-center justify-center rounded-xl border border-rose-100 bg-rose-50 text-rose-700 active:bg-rose-100 disabled:opacity-50"
            aria-label="Làm mới"
          >
            <RefreshCw
              size={16}
              className={refreshing ? "animate-spin" : undefined}
            />
          </button>
        }
      />

      <div className="space-y-5 px-5 pt-5 pb-4">
        {error ? (
          <div className="rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-[13px] text-rose-700">
            {error}
          </div>
        ) : null}

        <section>
          <MobileSectionHeader title="Tổng quan tháng" tone="rose" />
          {loading ? (
            <div className="grid grid-cols-2 gap-3">
              {[0, 1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="h-24 animate-pulse rounded-2xl bg-rose-100/50"
                />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <StatCard
                label="Cần bạn duyệt"
                value={counts.needApprove}
                icon={AlertTriangle}
                tone="rose"
                onClick={goNeedApprove}
              />
              <StatCard
                label="Việc hôm nay"
                value={counts.today}
                icon={Clock}
                tone="amber"
                onClick={goToday}
              />
              <StatCard
                label="Chờ duyệt"
                value={counts.waiting}
                icon={ClipboardList}
                tone="sky"
                onClick={goWaiting}
              />
              <StatCard
                label="Đã báo cáo"
                value={counts.reported}
                icon={FileCheck}
                tone="violet"
                onClick={goReported}
              />
            </div>
          )}
        </section>

        {!loading && needApprove.length > 0 ? (
          <section>
            <MobileSectionHeader
              title="Cần bạn xử lý"
              tone="rose"
              action={
                <button
                  type="button"
                  onClick={goNeedApprove}
                  className="text-[12px] font-medium text-rose-700"
                >
                  Xem tất cả
                </button>
              }
            />
            <div className="overflow-hidden rounded-2xl border border-rose-100/80 bg-white shadow-sm">
              <ul className="divide-y divide-rose-50">
                {needApprove.slice(0, HUB_PREVIEW_LIMIT).map((task) => (
                  <li key={`need-${task.id}-${task.work_day}`}>
                    <TaskRow task={task} onClick={goNeedApprove} />
                  </li>
                ))}
              </ul>
            </div>
          </section>
        ) : null}

        <section>
          <MobileSectionHeader title="Lối tắt" tone="slate" />
          <MobileNavGroup>
            <MobileNavRow
              icon={List}
              label="Danh sách đầy đủ"
              hint="Bộ lọc & bảng như trên máy tính"
              tone="slate"
              onClick={openFullList}
            />
            {canCreate ? (
              <MobileNavRow
                icon={ClipboardList}
                label="Tạo / quản lý công việc"
                hint="Mở trang công việc để đăng ký"
                tone="rose"
                onClick={openFullList}
              />
            ) : null}
          </MobileNavGroup>
        </section>

        {!loading && todayOpen.length > 0 ? (
          <section>
            <MobileSectionHeader
              title="Việc hôm nay còn mở"
              tone="amber"
              action={
                <button
                  type="button"
                  onClick={goToday}
                  className="text-[12px] font-medium text-amber-700"
                >
                  Xem tất cả
                </button>
              }
            />
            <div className="overflow-hidden rounded-2xl border border-amber-100/80 bg-white shadow-sm">
              <ul className="divide-y divide-amber-50">
                {todayOpen.slice(0, HUB_PREVIEW_LIMIT).map((task) => (
                  <li key={`today-${task.id}-${task.status}`}>
                    <TaskRow task={task} onClick={goToday} />
                  </li>
                ))}
              </ul>
            </div>
          </section>
        ) : null}

        {!loading && tasks.length === 0 && !error ? (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="pb-2 text-center text-[12px] text-gray-400"
          >
            Chưa có công việc trong tháng này
          </motion.p>
        ) : null}
      </div>
    </MobilePageShell>
  );
}
